import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { MagnifyIcon } from 'Common/Icons/Magnify';

import styles from './index.module.css';
import { CustomInputSearch } from '@/Components/CustomInputSearch';
import { LanguageContext } from '@/context/languageContext';
import { useDebounce, useRequest, useUpdateEffect } from 'ahooks';
import { getCategoryCatalog } from '@/lib/api/methods';
import { AutoComplete } from 'antd';
import { highlightText } from '@/helpers/help';
import { useChangingUrlTrigger } from '@/hooks/useChangingUrlTrigger';
import { useRouter } from 'next/navigation';
import { NotFoundSearch } from './Components/NotFoundSearch';
import { CityContext } from '@/context/cityContext';

const SearchBox = () => {
  const { t, language, countryCode } = useContext(LanguageContext);
  const [searchText, setSearchText] = useState('');
  const { changeUrlData } = useChangingUrlTrigger({ isSearchFilter: false });
  const router = useRouter();
  const prevSearchText = useRef('');
  const { choosenCity, setIsRestrictionOpen } = useContext(CityContext);

  const {
    data: searchData,
    runAsync: search,
    loading: searchLoading,
  } = useRequest(() => getCategoryCatalog(searchText || ''), {
    manual: true,
    ready: prevSearchText.current !== searchText,
    onError: (error: any) => {
      if (error?.response?.data?.message) {
        console.error('error', error.response.data.message);
      } else {
        console.error('error', error.message);
      }
      prevSearchText.current = searchText;
    },
    onSuccess: () => {
      prevSearchText.current = searchText;
    },
    debounceMaxWait: 500,
  });

  useUpdateEffect(() => {
    if (!language || !countryCode) return;
    search();
  }, [language, countryCode]);

  const options = useMemo(() => {
    if (!searchData) return [];
    return searchData.map((item, i) => {
      let link = `${changeUrlData?.link}${
        item.search_catalog.categoryPath?.length
          ? '/' +
            item.search_catalog.categoryPath.map((cat) => cat.slug).join('/')
          : ''
      }${
        item.search_catalog.productGroups?.length
          ? '/' +
            item.search_catalog.productGroups.map((gr) => gr.slug).join('/')
          : ''
      }`;

      if (item.search_catalog.options.length) {
        let resultSearch: string = '';

        const opts = {
          fmo: {},
          fo: {},
          fon: {},
        };

        item.search_catalog.options.forEach((opt) => {
          if (
            (opt.type === 'MultiSelect' || opt.type === 'Select') &&
            opts.fmo[opt.id]
          ) {
            opts.fmo[opt.id].push();
          } else if (opt.type === 'MultiSelect' || opt.type === 'Select') {
            opts.fmo[opt.id] = [opt.selectValueId];
          }

          if (opt.type === 'String') {
            opts.fo[opt.id] = Array.isArray(opt.value)
              ? opt.value
              : [opt.value];
          }

          if (opt.type === 'Numeric') {
            opts.fon[opt.id] = opt.value;
          }
        });

        const fo = Object.entries(opts.fo);
        const fmo = Object.entries(opts.fmo);
        const fon = Object.entries(opts.fon);

        if (fo.length) {
          resultSearch = `fo={${fo
            .map((opt) => `"${opt[0]}":["${opt[1]}"]`)
            .join(',')}}`;
        }

        if (fmo.length) {
          resultSearch = resultSearch
            ? `${resultSearch}&fmo={${fmo
                .map(
                  (opt) =>
                    `"${opt[0]}":[${(opt[1] as any[])
                      .map((v) => `"${v}"`)
                      .join(',')}]`
                )
                .join(',')}}`
            : `fmo={${fmo
                .map(
                  (opt) =>
                    `"${opt[0]}":[${(opt[1] as any[])
                      .map((v) => `"${v}"`)
                      .join(',')}]`
                )
                .join(',')}}`;
        }

        if (fon.length) {
          resultSearch = resultSearch
            ? `${resultSearch}&fon={${fon
                .map((opt) => `"${opt[0]}":[${opt[1]},null]`)
                .join(',')}}`
            : `fon={${fon
                .map((opt) => `"${opt[0]}":[${opt[1]},null]`)
                .join(',')}}`;
        }

        link += `?${resultSearch}`;
      }
      return {
        value: link, // значение в инпуте при выборе
        label: (() => {
          const ammountLetters = Object.values(item.search_catalog).reduce(
            (all, curr) => {
              return (
                all +
                curr.reduce((all1, curr1) => {
                  return all1 + curr1.name.length;
                }, 0)
              );
            },
            0
          );

          const amountOfImages =
            Object.entries(item.search_catalog).reduce((all, curr) => {
              if (curr[0] === 'options') return all;
              return all + curr[1].length;
            }, 0) - 1;

          const optionsWrapperGaps =
            Object.values(item.search_catalog).filter((v) => v?.length).length -
            1;

          const widthOfImages = amountOfImages > 0 ? amountOfImages * 18 : 0;
          const everyOptionGapsWidth =
            amountOfImages > 0 ? amountOfImages * 5 : 0;
          const optionsWrapperGapsWidth =
            optionsWrapperGaps > 0 ? optionsWrapperGaps * 5 : 0;
          const amountLettersWidth = ammountLetters * 7.5;

          const allWidth =
            amountLettersWidth +
            widthOfImages +
            everyOptionGapsWidth +
            optionsWrapperGapsWidth;

          const v = (
            <div className={styles.optionsWrapper}>
              {allWidth > 450 ? (
                item.search_catalog.productGroups.length ? (
                  <>
                    <div>
                      <span>...</span>
                      <img src="/assets/svg/CaretRight.svg" alt="caret right" />
                    </div>
                    <div className={styles.everyOption}>
                      {
                        <div
                          key={
                            item.search_catalog.productGroups?.at(-1).id +
                            item.search_catalog.productGroups?.at(-1).slug
                          }
                        >
                          <span>
                            {highlightText(
                              item.search_catalog.productGroups?.at(-1).name,
                              searchText,
                              styles.highlight
                            )}
                            {item.search_catalog.options?.length ? (
                              <span>:</span>
                            ) : null}
                          </span>
                        </div>
                      }
                    </div>
                    {item.search_catalog.options?.length ? (
                      <div className={styles.everyOption}>
                        {item.search_catalog.options?.map((v, i) => {
                          const isLast =
                            item.search_catalog.options.length - 1 === i;

                          if (isLast)
                            return (
                              <div key={v.id + v.value}>
                                {highlightText(
                                  v.value,
                                  searchText,
                                  styles.highlight
                                )}
                              </div>
                            );
                          return (
                            <div key={v.id + v.value}>
                              <span>
                                {highlightText(
                                  v.value,
                                  searchText,
                                  styles.highlight
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className={styles.everyOption}>
                    {item.search_catalog.categoryPath?.map((v, i) => {
                      const isLast =
                        item.search_catalog.categoryPath.length - 1 === i;

                      if (isLast)
                        return (
                          <div key={v.id + v.slug}>
                            {highlightText(
                              v.name,
                              searchText,
                              styles.highlight
                            )}
                          </div>
                        );
                      return (
                        <div key={v.id + v.slug}>
                          <span>
                            {highlightText(
                              v.name,
                              searchText,
                              styles.highlight
                            )}
                          </span>
                          <img
                            src="/assets/svg/CaretRight.svg"
                            alt="caret right"
                          />
                        </div>
                      );
                    })}
                    {item.search_catalog?.productGroups?.length ? (
                      <img src="/assets/svg/CaretRight.svg" alt="caret right" />
                    ) : null}
                  </div>
                )
              ) : (
                <>
                  {item.search_catalog.categoryPath?.length ? (
                    <div className={styles.everyOption}>
                      {item.search_catalog.categoryPath?.map((v, i) => {
                        const isLast =
                          item.search_catalog.categoryPath.length - 1 === i;

                        if (isLast)
                          return (
                            <div key={v.id + v.slug}>
                              {highlightText(
                                v.name,
                                searchText,
                                styles.highlight
                              )}
                            </div>
                          );
                        return (
                          <div key={v.id + v.slug}>
                            <span>
                              {highlightText(
                                v.name,
                                searchText,
                                styles.highlight
                              )}
                            </span>
                            <img
                              src="/assets/svg/CaretRight.svg"
                              alt="caret right"
                            />
                          </div>
                        );
                      })}
                      {item.search_catalog?.productGroups?.length ? (
                        <img
                          src="/assets/svg/CaretRight.svg"
                          alt="caret right"
                        />
                      ) : null}
                    </div>
                  ) : null}
                  {item.search_catalog.productGroups?.length ? (
                    <div className={styles.everyOption}>
                      {item.search_catalog.productGroups?.map((v, i) => {
                        const isLast =
                          item.search_catalog.productGroups.length - 1 === i;

                        if (isLast)
                          return (
                            <div key={v.id + v.slug}>
                              {highlightText(
                                v.name,
                                searchText,
                                styles.highlight
                              )}
                              {item.search_catalog.options?.length ? (
                                <span>:</span>
                              ) : null}
                            </div>
                          );
                        return (
                          <div key={v.id + v.slug}>
                            <span>
                              {highlightText(
                                v.name,
                                searchText,
                                styles.highlight
                              )}
                            </span>
                            <img
                              src="/assets/svg/CaretRight.svg"
                              alt="caret right"
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  {item.search_catalog.options?.length ? (
                    <div className={styles.everyOption}>
                      {item.search_catalog.options?.map((v, i) => {
                        const isLast =
                          item.search_catalog.options.length - 1 === i;

                        if (isLast)
                          return (
                            <div key={v.id + v.value}>
                              {highlightText(
                                v.value,
                                searchText,
                                styles.highlight
                              )}
                            </div>
                          );
                        return (
                          <div key={v.id + v.value}>
                            <span>
                              {highlightText(
                                v.value,
                                searchText,
                                styles.highlight
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          );
          return v;
        })(),
      };
    });
  }, [searchData, searchText, language, countryCode]);

  const handleSelect = (value) => {
    router.push(value);
  };

  const refauto = useRef<any>();

  const searchTextDebounce = useDebounce(searchText?.trim(), { wait: 300 });

  useEffect(() => {
    search();
  }, [searchTextDebounce]);

  return (
    <AutoComplete
      ref={refauto}
      onOpenChange={(visible) => {
        if (visible)
          refauto.current.nativeElement.style.border = '1px solid #FFB620';
        if (!visible) refauto.current.nativeElement.style.border = '';
      }}
      options={options}
      onSelect={handleSelect}
      showSearch={false}
      classNames={{ popup: { root: styles.popup } }}
      className={styles.itemsSelector}
      value={searchText}
      notFoundContent={
        searchTextDebounce ? <NotFoundSearch isLoading={searchLoading} /> : null
      }
      onKeyUp={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <CustomInputSearch
        placeholder={t('whatToRent')}
        enterButton={
          <MagnifyIcon
            style={{
              backgroundSize: 16,
              backgroundPosition: 'center',
            }}
          />
        }
        onSearch={() => {
          if (options?.[0]?.value) handleSelect(options[0].value);
        }}
        value={searchText}
        onChange={({ target: { value } }) => {
          if (choosenCity.length === 2) {
            setSearchText(value);
          } else {
            setIsRestrictionOpen(true);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        className={styles.input}
        type="large"
      />
    </AutoComplete>
  );
};

export default SearchBox;
